/**
 * Admin › Race Engine — inspector for the ingestion runner, the hot live store,
 * the layout registry and every archived session's raw store / replay build.
 * Reads /api/race-engine/admin/inspector; actions call rebuild-replay and resync.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

const REFRESH_MS = 15000;

function text(value, fallback = "-") {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "boolean") return value ? "yes" : "no";
    if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
    return String(value);
}

function tone(value) {
    const normalized = String(value ?? "").toLowerCase();
    if (["ok", "live", "ready", "replay_ready", "memory", "redis", "valid"].includes(normalized)) return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    if (["stalled", "standby", "post_session", "prepared", "provider_unavailable", "warning"].includes(normalized)) return "border-amber-500/30 bg-amber-500/10 text-amber-200";
    if (["error", "subscription_required", "failed", "invalid"].includes(normalized)) return "border-red-500/30 bg-red-500/10 text-red-200";
    return "border-zinc-500/30 bg-zinc-500/10 text-zinc-300";
}

function structuralStatus(validation) {
    if (!validation) return "unchecked";
    if (validation.closed === false || validation.length_ok === false) return "invalid";
    return "valid";
}

function Chip({ value, label }) {
    return (
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-mono text-[11px] ${tone(value)}`}>
            {label ? `${label}: ` : ""}
            {text(value)}
        </span>
    );
}

function Panel({ kicker, title, description, action, children }) {
    return (
        <section className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{kicker}</p>
                    <h2 className="mt-2 font-display text-3xl tracking-tight text-white">{title}</h2>
                    {description ? <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-400">{description}</p> : null}
                </div>
                {action}
            </div>
            <div className="mt-5">{children}</div>
        </section>
    );
}

function Table({ columns, rows, empty = "Nothing to show." }) {
    if (!rows.length) return <p className="text-sm text-zinc-500">{empty}</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
                <thead className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} className="pb-2 pr-4 font-medium">{column.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-zinc-300">
                    {rows.map((row) => (
                        <tr key={row.key}>
                            {columns.map((column) => (
                                <td key={column.key} className="py-2 pr-4 align-top">{column.render ? column.render(row) : text(row[column.key])}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminRaceEnginePage() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState({});
    const [messages, setMessages] = useState([]);

    const load = useCallback(async () => {
        try {
            const payload = await adminApi.raceEngine.inspector();
            setData(payload);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Inspector unavailable.");
        }
    }, []);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const act = async (key, fn) => {
        setBusy((prev) => ({ ...prev, [key]: true }));
        try {
            const result = await fn();
            setMessages((prev) => [`${new Date().toLocaleTimeString()} ${key}: ${text(result?.status || result?.message || "done")}`, ...prev].slice(0, 8));
            await load();
        } catch (err) {
            setMessages((prev) => [`${new Date().toLocaleTimeString()} ${key} failed: ${err?.response?.data?.detail?.message || err?.message}`, ...prev].slice(0, 8));
        } finally {
            setBusy((prev) => ({ ...prev, [key]: false }));
        }
    };

    const live = useMemo(() => data?.live || {}, [data]);
    const streams = useMemo(() => Object.entries(live?.live?.streams || {}).map(([endpoint, health]) => ({ key: endpoint, endpoint, ...health })), [live]);
    const layouts = useMemo(() => (data?.layouts || []).map((layout) => ({ key: layout.layout_id, ...layout })), [data]);
    const sessions = useMemo(
        () =>
            (data?.sessions || [])
                .map((item) => ({ key: String(item.session_key), ...item }))
                .sort((a, b) => Number(b.session_key) - Number(a.session_key)),
        [data]
    );

    return (
        <div className="space-y-6" data-testid="admin-race-engine">
            <Panel
                kicker="Race engine"
                title="Ingestion & publication"
                description="Single-owner live runner, hot store, layout registry and per-session raw archives. Everything the public race views read comes from here."
                action={
                    <div className="flex items-center gap-3">
                        <span className="font-mono text-[11px] text-zinc-500">{data?.generated_at ? `updated ${new Date(data.generated_at).toLocaleTimeString()}` : ""}</span>
                        <Button type="button" variant="outline" onClick={load}>Refresh</Button>
                    </div>
                }
            >
                {error ? <p className="text-sm text-red-300">{text(error)}</p> : null}
                <div className="flex flex-wrap gap-2">
                    <Chip label="runner" value={live.status} />
                    <Chip label="session" value={live.session_key} />
                    <Chip label="owner" value={live.owner || live.live?.owner} />
                    <Chip label="store" value={data?.store?.backend} />
                    {data?.store?.last_error ? <Chip label="store error" value={data.store.last_error} /> : null}
                    <Chip label="sequence" value={data?.live_snapshot_meta?.sequence} />
                    <Chip label="watermark" value={data?.live_snapshot_meta?.clock?.watermark?.event_time || data?.live_snapshot_meta?.clock?.event_time} />
                    <Chip label="phase" value={data?.live_snapshot_meta?.phase?.phase} />
                </div>
                {live.message ? <p className="mt-3 text-sm text-zinc-400">{text(live.message)}</p> : null}
                <div className="mt-5">
                    <Table
                        columns={[
                            { key: "endpoint", label: "Stream" },
                            { key: "status", label: "Status", render: (row) => <Chip value={row.status} /> },
                            { key: "lag_seconds", label: "Lag (s)" },
                            { key: "rows_total", label: "Rows" },
                            { key: "last_rows", label: "Last batch" },
                            { key: "consecutive_errors", label: "Errors" },
                            { key: "last_event_time", label: "Last event" },
                            { key: "last_error", label: "Last error" },
                        ]}
                        rows={streams}
                        empty="No live session is being ingested."
                    />
                </div>
                {messages.length ? (
                    <ul className="mt-4 space-y-1 font-mono text-[11px] text-zinc-400">
                        {messages.map((message) => (
                            <li key={message}>{message}</li>
                        ))}
                    </ul>
                ) : null}
            </Panel>

            <Panel kicker="Archive" title="Sessions" description="Raw observation stores (append-only, revisioned) and the replay build currently published for each one. Rebuild is idempotent: an unchanged archive yields the same build id.">
                <Table
                    columns={[
                        { key: "session_key", label: "Session" },
                        { key: "store_revision", label: "Store rev", render: (row) => text(row.raw?.store_revision) },
                        { key: "updated_at", label: "Archive updated", render: (row) => text(row.raw?.updated_at) },
                        { key: "endpoints", label: "Endpoints", render: (row) => text(Object.keys(row.raw?.endpoints || {}).length) },
                        { key: "rows", label: "Rows", render: (row) => text(Object.values(row.raw?.endpoints || {}).reduce((sum, item) => sum + (item?.rows || 0), 0)) },
                        { key: "build", label: "Replay build", render: (row) => (row.replay?.build_id ? <Chip value={row.replay.build_id.slice(0, 12)} label="build" /> : <Chip value="none" />) },
                        { key: "published", label: "Published", render: (row) => text(row.replay?.published_at) },
                        {
                            key: "actions",
                            label: "Actions",
                            render: (row) => (
                                <div className="flex gap-2">
                                    <Button type="button" size="sm" variant="outline" disabled={!!busy[`rebuild:${row.session_key}`]} onClick={() => act(`rebuild:${row.session_key}`, () => adminApi.raceEngine.rebuildReplay(row.session_key))}>
                                        Rebuild
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" disabled={!!busy[`force:${row.session_key}`]} onClick={() => act(`force:${row.session_key}`, () => adminApi.raceEngine.rebuildReplay(row.session_key, { force: true }))}>
                                        Force
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" disabled={!!busy[`resync:${row.session_key}`]} onClick={() => act(`resync:${row.session_key}`, () => adminApi.raceEngine.resync(row.session_key))}>
                                        Resync
                                    </Button>
                                </div>
                            ),
                        },
                    ]}
                    rows={sessions}
                    empty="No archived sessions on this host."
                />
            </Panel>

            <Panel kicker="Geometry" title="Layout registry" description="Calibrated circuit layouts with their coverage and structural validation. Replays and live maps only draw layouts listed here.">
                <Table
                    columns={[
                        { key: "layout_id", label: "Layout" },
                        { key: "seasons", label: "Seasons", render: (row) => text((row.seasons || []).join(", ")) },
                        { key: "coverage", label: "Coverage", render: (row) => text(Object.entries(row.coverage || {}).filter(([, ok]) => ok).map(([name]) => name).join(", ")) },
                        { key: "validation", label: "Structural", render: (row) => <Chip value={structuralStatus(row.validation)} /> },
                        { key: "length", label: "Length (m)", render: (row) => text(row.validation?.length_m) },
                        { key: "intersections", label: "Crossovers", render: (row) => text(row.validation?.self_intersections) },
                        { key: "sessions", label: "Sessions", render: (row) => text((row.sessions || []).length) },
                    ]}
                    rows={layouts}
                />
            </Panel>
        </div>
    );
}
